import { Box, Grid, useTheme } from "@mui/material";
import * as React from "react";

import {
  DraggableDroppable,
  DragItem,
} from "@/shared/components/visualization/DnD";
import {
  FileSystemNode,
  isDirectory,
  isFile,
} from "@/shared/domain/files/filesystem";
import { FileSystemNodeType } from "@/shared/domain/files/filesystem/fileSystemNode";

import styles from "./DatasetFileGrid.module.css";
import { DatasetGridItem } from "./DatasetGridItem";
import { NoFilesFoundAlert } from "./NoFilesFoundAlert";

interface DatasetFileGridProps {
  datasetId: string;
  currentPage: FileSystemNode[] | null;
  handleRowClick?: (row: FileSystemNode) => void;
  isSearching: boolean;
  gridItems: number;
  annotationData?: object;
  selectedItems?: FileSystemNode[];
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  emptyContent?: React.ReactNode;
}

export const DatasetFileGrid: React.FC<DatasetFileGridProps> = ({
  datasetId,
  currentPage,
  handleRowClick,
  isSearching,
  gridItems,
  selectedItems,
  handleSelectRows,
  emptyContent,
}) => {
  const theme = useTheme();

  let datasetGridItemContent = null;

  if (currentPage && currentPage.length > 0) {
    datasetGridItemContent = (
      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        <>
          {currentPage.map((row) => {
            let key = "";
            let isChecked = false;

            if (isFile(row)) {
              key = row.file.file_id;
              isChecked =
                selectedItems?.some((item) => {
                  if (isFile(item)) {
                    return item.file.file_id === row.file.file_id;
                  } else {
                    return false;
                  }
                }) || false;
            } else if (isDirectory(row)) {
              key = row.directory.name;
              isChecked =
                selectedItems?.some((item) => {
                  if (isDirectory(item)) {
                    return item.directory.name === row.directory.name;
                  } else {
                    return false;
                  }
                }) || false;
            }

            return (
              <Grid
                item
                xs={2}
                sm={3}
                md={gridItems}
                key={key}
                sx={{
                  userSelect:
                    row.type !== FileSystemNodeType.File ? "none" : "auto",
                }}
              >
                <DraggableDroppable
                  as={"div"}
                  id={row.name}
                  item={{ data: row, type: DragItem.GridFSNode }}
                  key={row.name}
                  isDroppable={isDirectory(row)}
                  className={[styles["tr-grid-size"]]}
                  isSelected={isChecked}
                >
                  <DatasetGridItem
                    item={row}
                    datasetId={datasetId}
                    onClick={() => {
                      if (
                        row.type !== FileSystemNodeType.File &&
                        handleRowClick
                      ) {
                        handleRowClick(row);
                      }
                    }}
                    isChecked={isChecked}
                    handleSelectRows={handleSelectRows}
                  />
                </DraggableDroppable>
              </Grid>
            );
          })}
        </>
      </Grid>
    );
  } else if (currentPage && currentPage.length === 0) {
    datasetGridItemContent = isSearching ? (
      <NoFilesFoundAlert />
    ) : (
      emptyContent ?? null
    );
  }

  return (
    <Box
      sx={{
        mt: theme.spacing(2),
        transition: "opacity 0.2s",
        minHeight: "50px",
        overflowY: "auto",
        flex: 1,
      }}
    >
      {datasetGridItemContent}
    </Box>
  );
};
